/**
 * What a bag weighs and what a back can take.
 *
 * Weight is the only limit on a container. There is no grid and no slot count: a plate carrier
 * costs six and a half kilos of the same budget a hundred and fifty rounds cost, so a full backpack
 * is a decision about what to leave behind rather than an exercise in packing. That decision is
 * what gives the death-drop rule its teeth.
 *
 * Strength is the one stat that moves the budget, and by how much is declared in `statContract.ts`
 * with everything else a stat moves.
 */

import { BASELINE_STAT, CARRY_KG_PER_STRENGTH, statValue } from '@/sim/combat/statContract';
import { itemDef } from '@/sim/items/itemRegistry';
import { EMPTY_EFFECTS, effectScale } from '@/sim/effects/characterEffects';
import type { EffectBundle } from '@/sim/effects/characterEffects';
import type { StatBlock } from '@/sim/combat/statContract';
import type { Container, ItemStack } from '@/sim/inventory/inventoryContract';

/** What a character of baseline Strength can carry into a raid. */
export const BASE_CARRY_KG = 25;

/** What the hub holds. Large enough to hoard, small enough that hoarding ends somewhere. */
export const STASH_CAPACITY_KG = 500;

/** Below this many kilos apart, two weights are the same weight. Floats add up over a backpack. */
export const WEIGHT_EPSILON = 1e-6;

/**
 * The backpack budget for a character. Never negative, whatever a save claims Strength is.
 *
 * Strength adds kilos and the lever multiplies the result, which is deliberately two different
 * shapes onto one number: a stat point is worth the same three kilos to everybody, and a perk is
 * worth more to somebody already strong. Taking both is the intended stack, and neither knows the
 * other exists.
 */
export function carryCapacityKg(stats: StatBlock, effects: EffectBundle = EMPTY_EFFECTS): number {
  const strength = statValue(stats, 'strength');
  const earned = BASE_CARRY_KG + CARRY_KG_PER_STRENGTH * (strength - BASELINE_STAT);

  return Math.max(0, earned * effectScale(effects, 'carryKg'));
}

/**
 * What one stack weighs. A nonsense weight or a nonsense count reads as weightless rather than as
 * NaN, because one bad stack out of a save would otherwise make a whole backpack impossible to add
 * up and impossible to empty.
 */
export function stackWeightKg(stack: ItemStack): number {
  const perUnit = itemDef(stack.defId).weightKg;
  if (!Number.isFinite(perUnit) || perUnit <= 0) return 0;
  if (!Number.isFinite(stack.count) || stack.count <= 0) return 0;
  return perUnit * stack.count;
}

/** What a container weighs, summed in stack order so two peers add the floats the same way. */
export function containerWeightKg(container: Container): number {
  let total = 0;
  for (const stack of container.stacks) total += stackWeightKg(stack);
  return total;
}

export function remainingCapacityKg(container: Container): number {
  return Math.max(0, container.capacityKg - containerWeightKg(container));
}

/** Whether `addedKg` more fits. The epsilon stops a stack failing to go back where it was. */
export function fitsInContainer(container: Container, addedKg: number): boolean {
  return containerWeightKg(container) + addedKg <= container.capacityKg + WEIGHT_EPSILON;
}
